import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/authContext";
import { Helmet } from "react-helmet-async";
import { fetchAp2025PharmacyColleges, saveSelectionAPI } from "../api/getApi";
import MobileFilterDrawer from "../components/mobileFilterDrawer";

const CreateAP2025PharList = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  // --- State Management ---
  const [allColleges, setAllColleges] = useState([]);
  const [filteredColleges, setFilteredColleges] = useState([]);
  const [selectedColleges, setSelectedColleges] = useState([]);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isSaved, setIsSaved] = useState(false);

  // Filtering State
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPlaces, setSelectedPlaces] = useState([]);
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [selectedDistricts, setSelectedDistricts] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);

  // UI State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalInput, setModalInput] = useState("");
  const [placeSearchTerm, setPlaceSearchTerm] = useState("");
  const [branchSearchTerm, setBranchSearchTerm] = useState("");
  const [districtSearchTerm, setDistrictSearchTerm] = useState("");
  const [isPlaceDrawerOpen, setIsPlaceDrawerOpen] = useState(false);
  const [isBranchDrawerOpen, setIsBranchDrawerOpen] = useState(false);
  const [isDistrictDrawerOpen, setIsDistrictDrawerOpen] = useState(false);

  // --- Effects ---

  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/signup", { state: { message: "You must be logged in to create a list." } });
    }
  }, [isAuthenticated, navigate]);

  // Fetch initial college data
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true);
        const data = await fetchAp2025PharmacyColleges();
        setAllColleges(data);
        setFilteredColleges(data); // Initially, show all
      } catch (err) {
        setError("Failed to load college data. Please try again later.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    loadInitialData();
  }, []);

    useEffect(() => {
      window.scrollTo(0, 0);
    }, []);

  // Apply filters whenever dependencies change
  useEffect(() => {
    const filtered = allColleges.filter(college => {
        const meetsSearch = searchTerm ? (college.inst_name.toLowerCase().includes(searchTerm.toLowerCase()) || college.inst_code.toLowerCase().includes(searchTerm.toLowerCase()) || college.place.toLowerCase().includes(searchTerm.toLowerCase()) || college.branch_code.toLowerCase().includes(searchTerm.toLowerCase())) : true;
        const meetsPlace = selectedPlaces.length > 0 ? selectedPlaces.includes(college.place) : true;
        const meetsBranch = selectedBranches.length > 0 ? selectedBranches.includes(college.branch_code) : true;
        const meetsDistrict = selectedDistricts.length > 0 ? selectedDistricts.includes(college.dist) : true;
        const meetsType = selectedTypes.length > 0 ? selectedTypes.includes(college.type) : true;
        return meetsSearch && meetsPlace && meetsBranch && meetsDistrict && meetsType;
    });
    setFilteredColleges(filtered);
  }, [allColleges, searchTerm, selectedPlaces, selectedBranches, selectedDistricts, selectedTypes]);

  // --- Handlers ---

  const handleBack = () => {
    if (selectedColleges.length > 0 && !isSaved) {
      if (window.confirm("You have unsaved changes. Are you sure you want to go back? Your list will be lost.")) {
        navigate(-1);
      }
    } else {
      navigate(-1);
    }
  };

  const handleSaveAndDownload = () => {
    if (!isAuthenticated) {
      navigate("/signup", { state: { message: "Please log in to save your list." } });
      return;
    }
    if (selectedColleges.length === 0) {
      alert("Please select at least one college to save.");
      return;
    }
    setIsModalOpen(true);
  };

  const handleModalSubmit = async () => {
    const listName = modalInput.trim() || `My AP 2025 Pharmacy List - ${new Date().toLocaleDateString()}`;
    try {
      const data = await saveSelectionAPI(selectedColleges, { note: "List created without rank criteria" }, listName);
      if (data.success) {
        setIsSaved(true);
        navigate("/review-report", {
          state: {
            selectedColleges,
            formData: { note: "List created without rank criteria" },
            favoriteIndex: data.favoriteIndex,
          },
        });
      } else {
        setError("Failed to save the list. Please try again.");
      }
    } catch (err) {
      setError("An error occurred while saving.");
      console.error(err);
    } finally {
      setIsModalOpen(false);
      setModalInput("");
    }
  };

  const handlePlaceFilterChange = (place) => {
    setSelectedPlaces(prev => 
      prev.includes(place) ? prev.filter(p => p !== place) : [...prev, place]
    );
  };

  const handleBranchFilterChange = (branch) => {
    setSelectedBranches(prev => 
      prev.includes(branch) ? prev.filter(b => b !== branch) : [...prev, branch]
    );
  };

  const handleDistrictFilterChange = (district) => {
    setSelectedDistricts(prev => 
      prev.includes(district) ? prev.filter(d => d !== district) : [...prev, district]
    );
  };

  // --- List Management Actions ---

  const addCollege = (college) => {
    if (!selectedColleges.find(c => c._id === college._id)) {
      setSelectedColleges(prev => [...prev, college]);
      setIsSaved(false);
    }
  };

  const removeCollege = (collegeToRemove) => {
    setSelectedColleges(prev => prev.filter(c => c._id !== collegeToRemove._id));
    setIsSaved(false);
  };

  const moveCollegeUp = (index) => {
    if (index > 0) {
      const newSelected = [...selectedColleges];
      [newSelected[index - 1], newSelected[index]] = [newSelected[index], newSelected[index - 1]];
      setSelectedColleges(newSelected);
      setIsSaved(false);
    }
  };

  const moveCollegeDown = (index) => {
    if (index < selectedColleges.length - 1) {
      const newSelected = [...selectedColleges];
      [newSelected[index + 1], newSelected[index]] = [newSelected[index], newSelected[index + 1]];
      setSelectedColleges(newSelected);
      setIsSaved(false);
    }
  };

  // --- Memoized Values for Performance ---
  const uniquePlaces = useMemo(() => [...new Set(allColleges.map(c => c.place))].sort(), [allColleges]);
  const uniqueBranches = useMemo(() => [...new Set(allColleges.map(c => c.branch_code))].sort(), [allColleges]);
  const uniqueDistricts = useMemo(() => [...new Set(allColleges.map(c => c.dist).filter(Boolean))].sort(), [allColleges]);
  const uniqueTypes = useMemo(() => [...new Set(allColleges.map(c => c.type))].sort(), [allColleges]);

  const filteredPlaces = uniquePlaces.filter(place => place.toLowerCase().includes(placeSearchTerm.toLowerCase()));
  const filteredBranches = uniqueBranches.filter(branch => branch.toLowerCase().includes(branchSearchTerm.toLowerCase()));
  const filteredDistricts = uniqueDistricts.filter(district => district.toLowerCase().includes(districtSearchTerm.toLowerCase()));

  // --- Render ---

  if (loading) {
    return <div className="flex justify-center items-center h-screen"><div>Loading college data...</div></div>;
  }

  if (error) {
    return <div className="flex justify-center items-center h-screen text-red-500">{error}</div>;
  }

  return (
    <div className="bg-gray-50 min-h-screen p-4 sm:p-6 lg:p-8">
      <Helmet>
        <title>Create AP 2025 Pharmacy List | CollegeRank</title>
        <meta name="description" content="Create a personalized list of AP 2025 Pharmacy colleges." />
      </Helmet>

      <div className="max-w-7xl mx-auto">
        {/* --- Header Section --- */}
        <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200 mb-3">
          <h1 className="text-2xl font-bold text-gray-800 mb-2">AP 2025 Pharmacy College List</h1>
          <p className="text-gray-600">Browse and select colleges to create your personalized list.</p>
        </div>

        {/* --- Filter Section --- */}
        <div className="bg-white p-4 rounded-xl shadow-lg border border-gray-200 mb-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-3">
              <input type="text" placeholder="Search by college, branch, or location..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg"/>
            </div>
            <button onClick={() => setIsPlaceDrawerOpen(true)} className="w-full text-left bg-gray-100 p-2 rounded-lg hover:bg-gray-200">
              <div className="flex justify-between items-center">
                <div>
                  <span className="font-semibold">Places</span>
                  <span className="block text-sm text-gray-600">{selectedPlaces.length > 0 ? `${selectedPlaces.length} selected` : 'All'}</span>
                </div>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </div>
            </button>
            <button onClick={() => setIsBranchDrawerOpen(true)} className="w-full text-left bg-gray-100 p-2 rounded-lg hover:bg-gray-200">
              <div className="flex justify-between items-center">
                <div>
                  <span className="font-semibold">Branches</span>
                  <span className="block text-sm text-gray-600">{selectedBranches.length > 0 ? `${selectedBranches.length} selected` : 'All'}</span>
                </div>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </div>
            </button>
            <button onClick={() => setIsDistrictDrawerOpen(true)} className="w-full text-left bg-gray-100 p-2 rounded-lg hover:bg-gray-200">
              <div className="flex justify-between items-center">
                <div>
                  <span className="font-semibold">Districts</span>
                  <span className="block text-sm text-gray-600">{selectedDistricts.length > 0 ? `${selectedDistricts.length} selected` : 'All'}</span>
                </div>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </div>
            </button>
          </div>
        </div>

        {/* --- LISTS SECTION --- */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Available Colleges */}
          <div className="lg:col-span-2 bg-white py-4 px-3 rounded-xl shadow-lg border border-gray-200">
            <div className="flex justify-between items-center mb-4 px-1">
              <h2 className="text-xl font-bold text-gray-800">Available Colleges ({filteredColleges.length})</h2>
            </div>
            {/* College List Table */}
            <div className="overflow-auto h-[600px] border rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0 z-20">
                  <tr>
                    <th className="px-2 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[30%]">College</th>
                    <th className="px-2 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[25%]">Branch</th>
                    <th className="px-2 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[10%]">District</th>
                    <th className="px-2 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[15%]">Fee</th>
                    <th className="px-2 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[10%]">Seats</th>
                    <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-[10%] sticky right-0 bg-gray-50">Add</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredColleges.map(college => (
                    <tr key={college._id} className="hover:bg-gray-50 relative z-10">
                      <td className="px-2 sm:px-4 py-3 w-[30%]">
                        <div className="text-xs sm:text-sm font-medium text-gray-900">
                          <span>{college.inst_code} - </span>
                        </div>
                        <div className="text-xs sm:text-sm text-gray-900 overflow-hidden" style={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          textOverflow: 'ellipsis'
                        }}>
                          {college.inst_name}
                        </div>
                      </td>
                      <td className="px-2 sm:px-4 py-3 text-xs sm:text-sm w-[25%]">
                        <div className="truncate">{college.branch_code}</div>
                      </td>
                      <td className="px-2 sm:px-4 py-3 text-xs sm:text-sm w-[10%]">
                        {college.dist}
                      </td>
                      <td className="px-2 sm:px-4 py-3 text-xs sm:text-sm w-[15%]">
                        {college.college_fee ? `₹ ${college.college_fee.toLocaleString('en-IN')}` : 'N/A'}
                      </td>
                      <td className="px-2 sm:px-4 py-3 text-xs sm:text-sm w-[10%]">
                        {college.convener_seats ?? 'N/A'}
                      </td>
                      <td className="px-2 py-3 text-center w-[10%] sticky right-0 bg-white hover:bg-gray-50">
                        <button onClick={() => addCollege(college)} disabled={selectedColleges.some(c => c._id === college._id)} className="bg-green-500 text-white text-sm sm:text-base font-bold py-1 px-2 sm:px-3 rounded-full hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-transform transform hover:scale-110 relative z-30">
                          +
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Selected Colleges */}
          <div className="lg:col-span-1 bg-white p-6 rounded-xl shadow-lg border border-gray-200">
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-800">Your List ({selectedColleges.length})</h2>
            </div>
            <div className="overflow-auto h-[600px] border rounded-lg">
              {selectedColleges.length > 0 ? (
                <ul className="divide-y divide-gray-200">
                  {selectedColleges.map((college, index) => (
                    <li key={college._id} className="p-3 flex items-center justify-between hover:bg-gray-50">
                      <div className="flex-1 overflow-hidden">
                        <p className="text-sm font-medium text-gray-900 truncate">{index + 1}. {college.inst_name}</p>
                        <p className="text-xs text-gray-500">{college.branch_code} - {college.place}</p>
                      </div>
                      <div className="flex items-center space-x-1 ml-2">
                        <button onClick={() => moveCollegeUp(index)} disabled={index === 0} className="text-gray-400 hover:text-gray-700 disabled:opacity-30 p-1">▲</button>
                        <button onClick={() => moveCollegeDown(index)} disabled={index === selectedColleges.length - 1} className="text-gray-400 hover:text-gray-700 disabled:opacity-30 p-1">▼</button>
                        <button onClick={() => removeCollege(college)} className="text-red-500 hover:text-red-700 p-1">✖</button>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500 p-4 text-center">Add colleges from the left to build your list.</div>
              )}
            </div>
          </div>
        </div>

        {/* --- ACTION BUTTONS --- */}
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <button onClick={handleBack} className="w-full bg-gray-200 text-gray-800 font-semibold py-3 px-4 rounded-lg hover:bg-gray-300 transition">Back</button>
            <button onClick={handleSaveAndDownload} disabled={selectedColleges.length === 0} className="w-full bg-green-600 text-white font-semibold py-3 px-4 rounded-lg hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed">Save and Proceed</button>
        </div>
      </div>

      {/* Place Filter Drawer */}
      <MobileFilterDrawer
        isOpen={isPlaceDrawerOpen}
        onClose={() => setIsPlaceDrawerOpen(false)}
        title="Select Places"
        searchTerm={placeSearchTerm}
        onSearchChange={(e) => setPlaceSearchTerm(e.target.value)}
        filterItems={filteredPlaces}
        selectedItems={selectedPlaces}
        onItemToggle={handlePlaceFilterChange}
        searchPlaceholder="Search places..."
      />

      {/* Branch Filter Drawer */}
      <MobileFilterDrawer
        isOpen={isBranchDrawerOpen}
        onClose={() => setIsBranchDrawerOpen(false)}
        title="Select Branches"
        searchTerm={branchSearchTerm}
        onSearchChange={(e) => setBranchSearchTerm(e.target.value)}
        filterItems={filteredBranches}
        selectedItems={selectedBranches}
        onItemToggle={handleBranchFilterChange}
        searchPlaceholder="Search branches..."
      />

      {/* District Filter Drawer */}
      <MobileFilterDrawer
        isOpen={isDistrictDrawerOpen}
        onClose={() => setIsDistrictDrawerOpen(false)}
        title="Select Districts"
        searchTerm={districtSearchTerm}
        onSearchChange={(e) => setDistrictSearchTerm(e.target.value)}
        filterItems={filteredDistricts}
        selectedItems={selectedDistricts}
        onItemToggle={handleDistrictFilterChange}
        searchPlaceholder="Search districts..."
      />

      {/* --- MODAL FOR SAVING LIST --- */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
          <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-sm">
            <h3 className="text-lg font-medium mb-4">Enter a name for your list</h3>
            <input type="text" value={modalInput} onChange={(e) => setModalInput(e.target.value)} placeholder={`My AP Pharmacy List - ${new Date().toLocaleDateString()}`} className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-4"/>
            <div className="flex justify-end space-x-3">
              <button onClick={() => setIsModalOpen(false)} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300">Cancel</button>
              <button onClick={handleModalSubmit} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Save List</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateAP2025PharList;
